/**
 * Module dependencies.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from 'app/components/ui/alert-dialog';
import { Badge } from 'app/components/ui/badge';
import { Checkbox } from 'app/components/ui/checkbox';
import { MoreVertical, Trash2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * `Props` type.
 */

type Props = {
  item: any;
  needsReconnect: boolean;
  onRemove: (soft: boolean) => Promise<void>;
};

/**
 * Mask an IBAN / account id, keeping only the last four characters.
 */

function mask(value?: string): string {
  if (!value) {
    return '—';
  }

  const tail = value.slice(-4);

  return `·· ${tail}`;
}

/**
 * Two-letter fallback badge from the connection name.
 */

function initials(name?: string): string {
  if (!name) {
    return 'BK';
  }

  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('');
}

/**
 * Export `ConnectionCard` component.
 *
 * A single linked bank account: logo/initials, masked IBAN, balance, a status
 * badge and a single actions menu. "Remove connection" opens a confirm that can
 * optionally hard-delete the imported movements (the existing `soft` flag).
 */

export function ConnectionCard({ item, needsReconnect, onRemove }: Props): JSX.Element {
  const [translate] = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [alsoDeleteData, setAlsoDeleteData] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const connect = item?.bankAccountConnect ?? {};
  const name: string = connect.name ?? item.name ?? translate('common.labels.iban');
  const logo: string | undefined = connect.logo;

  const remove = async () => {
    setIsRemoving(true);

    try {
      // `soft` keeps the imported movements; hard-delete removes them too.
      await onRemove(!alsoDeleteData);

      setConfirmOpen(false);
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <div
      className={
        'flex items-center gap-4 rounded-md border border-border bg-background px-4 py-3 transition-colors hover:border-muted-foreground/30'
      }
    >
      {logo ? (
        <img alt={name} className={'h-9 w-9 shrink-0 rounded-lg object-contain'} src={logo} />
      ) : (
        <div
          className={
            'grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-xs font-semibold text-primary'
          }
        >
          {initials(name)}
        </div>
      )}

      <div className={'min-w-0 flex-1'}>
        <div className={'flex items-center gap-2'}>
          <span className={'truncate text-sm font-semibold'}>{name}</span>

          {needsReconnect ? (
            <Badge className={'border-transparent bg-amber-100 text-amber-700'}>
              {translate('bankAccounts.connections.expired')}
            </Badge>
          ) : (
            <Badge className={'border-transparent bg-emerald-100 text-emerald-700'}>
              {translate('bankAccounts.connections.connected')}
            </Badge>
          )}
        </div>

        <div className={'mt-0.5 truncate text-xs tabular-nums text-muted-foreground'}>
          {`${translate('common.labels.iban')} ${mask(item.iban)}`}
        </div>
      </div>

      <div className={'shrink-0 text-right tabular-nums'}>
        <div className={'text-sm font-semibold'}>{item.balance ?? '—'}</div>
      </div>

      <Popover onOpenChange={setMenuOpen} open={menuOpen}>
        <PopoverTrigger
          aria-label={translate('common.labels.actions')}
          className={
            'grid h-8 w-8 shrink-0 place-items-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          }
        >
          <MoreVertical className={'h-4 w-4'} />
        </PopoverTrigger>

        <PopoverContent align={'end'} className={'w-56 p-1.5'}>
          <button
            className={
              'flex w-full items-center gap-2.5 rounded-sm px-2.5 py-2 text-left text-sm text-destructive transition-colors hover:bg-destructive/10'
            }
            onClick={() => {
              setMenuOpen(false);
              setAlsoDeleteData(false);
              setConfirmOpen(true);
            }}
            type={'button'}
          >
            <Trash2 className={'h-4 w-4'} />

            {translate('bankAccounts.connections.remove')}
          </button>
        </PopoverContent>
      </Popover>

      <AlertDialog onOpenChange={setConfirmOpen} open={confirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{translate('bankAccounts.connections.removeTitle')}</AlertDialogTitle>

            <AlertDialogDescription>{translate('bankAccounts.connections.removeDescription')}</AlertDialogDescription>
          </AlertDialogHeader>

          <label className={'flex cursor-pointer items-center gap-2.5 rounded-md bg-muted/50 px-3 py-2.5 text-sm'}>
            <Checkbox checked={alsoDeleteData} onCheckedChange={value => setAlsoDeleteData(value === true)} />

            {translate('bankAccounts.connections.removeHardLabel')}
          </label>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>{translate('common.actions.cancel')}</AlertDialogCancel>

            <AlertDialogAction
              className={'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90'}
              disabled={isRemoving}
              onClick={event => {
                event.preventDefault();
                remove();
              }}
            >
              {translate('common.actions.remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
